import { Plus, X } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { validateFieldValue } from "../../config/fieldConfig/formValidation";
import { useFieldResolver } from "../../hooks/useFieldResolver";
import type { KYCFormData, KYCLicense } from "../../types";
import { FileUploadField } from "../form/FileUploadField";
import { FormField } from "../form/FormField";
import { FormSection } from "../form/FormSection";
import { DatePicker } from "../ui/date-picker";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

interface Step4Props {
	formData: KYCFormData;
	updateFormData: (updates: Partial<KYCFormData>) => void;
}

const CUSTOM_FIELDS = new Set([
	"licenses",
	"complianceOfficer",
	"riskAssessmentUrl",
	"complianceProgramUrl",
]);

const Step4Compliance: React.FC<Step4Props> = ({
	formData,
	updateFormData,
}) => {
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

	const { fields } = useFieldResolver(
		{
			incorporationCountry: formData.countryOfIncorporation,
			entityType: formData.entityType,
			flowType: "KYB",
		},
		formData as unknown as Record<string, unknown>,
	);

	const step4Fields = fields.filter(
		(f) => f.step === 4 && !CUSTOM_FIELDS.has(f.fieldId),
	);
	const formValues = formData as unknown as Record<string, unknown>;

	const handleChange = (fieldId: string, value: unknown) => {
		updateFormData({ [fieldId]: value } as Partial<KYCFormData>);
		if (fieldErrors[fieldId]) {
			setFieldErrors((prev) => {
				const next = { ...prev };
				delete next[fieldId];
				return next;
			});
		}
	};

	const handleBlur = (fieldId: string) => {
		const field = step4Fields.find((f) => f.fieldId === fieldId);
		if (!field) return;
		const err = validateFieldValue(field, formValues[fieldId], formValues);
		setFieldErrors((prev) => ({ ...prev, [fieldId]: err ?? "" }));
	};

	const addLicense = () => {
		const newLicense: KYCLicense = {
			id: `license-${Date.now()}`,
			licenseName: "",
			jurisdiction: "",
			expiryDate: "",
			licenseNumber: "",
		};
		updateFormData({
			licenses: [...formData.licenses, newLicense],
		});
	};

	const removeLicense = (id: string) => {
		updateFormData({
			licenses: formData.licenses.filter((l) => l.id !== id),
		});
	};

	const updateLicense = (id: string, updates: Partial<KYCLicense>) => {
		updateFormData({
			licenses: formData.licenses.map((l) =>
				l.id === id ? { ...l, ...updates } : l,
			),
		});
	};

	// Separate fields by section for custom ordering
	const regulatedEntityField = step4Fields.find(
		(f) => f.fieldId === "isRegulatedEntity",
	);
	const sanctionsFields = step4Fields.filter((f) => f.section === "screening");
	const blockchainFields = step4Fields.filter(
		(f) => f.section === "blockchain",
	);
	const enforcementFields = step4Fields.filter(
		(f) =>
			f.fieldId === "regulatoryEnforcement" ||
			f.fieldId === "enforcementDetails",
	);

	return (
		<div className="w-full">
			<FormSection title="Compliance & Risk">
				{/* Regulated Entity Toggle */}
				{regulatedEntityField && (
					<FormField
						key={regulatedEntityField.fieldId}
						config={regulatedEntityField}
						value={formValues[regulatedEntityField.fieldId]}
						onChange={handleChange}
						onBlur={handleBlur}
						error={fieldErrors[regulatedEntityField.fieldId] || undefined}
					/>
				)}

				{/* Licenses Section - appears right after regulated entity toggle when checked */}
				{formData.isRegulatedEntity && (
					<div className="my-5">
						<div className="mb-4 border-(--color-border) border-b pb-3">
							<h3 className="m-0 font-semibold text-(--color-foreground) text-sm">
								Licenses & Registrations
							</h3>
						</div>
						{formData.licenses.map((license) => (
							<div
								key={license.id}
								className="mb-3 rounded-sm border border-(--color-border) bg-(--color-muted) p-4"
							>
								<div className="mb-3 flex items-center justify-between">
									<span className="font-medium text-(--color-muted-foreground) text-[13px]">
										License Information
									</span>
									<button
										type="button"
										onClick={() => removeLicense(license.id)}
										className="flex cursor-pointer items-center border-none bg-transparent text-(--color-error-500)"
										aria-label="Remove license"
									>
										<X size={16} />
									</button>
								</div>
								<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
									<div className="space-y-1.5">
										<Label htmlFor={`license-name-${license.id}`}>
											License Name
											<span
												aria-hidden="true"
												className="ml-0.5 text-(--color-error-500)"
											>
												*
											</span>
										</Label>
										<Input
											id={`license-name-${license.id}`}
											type="text"
											value={license.licenseName}
											onChange={(e) =>
												updateLicense(license.id, {
													licenseName: e.target.value,
												})
											}
											required
										/>
									</div>
									<div className="space-y-1.5">
										<Label htmlFor={`license-jurisdiction-${license.id}`}>
											Jurisdiction
											<span
												aria-hidden="true"
												className="ml-0.5 text-(--color-error-500)"
											>
												*
											</span>
										</Label>
										<Input
											id={`license-jurisdiction-${license.id}`}
											type="text"
											value={license.jurisdiction}
											onChange={(e) =>
												updateLicense(license.id, {
													jurisdiction: e.target.value,
												})
											}
											required
										/>
									</div>
								</div>
								<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
									<div className="space-y-1.5">
										<Label htmlFor={`license-number-${license.id}`}>
											License Number
											<span
												aria-hidden="true"
												className="ml-0.5 text-(--color-error-500)"
											>
												*
											</span>
										</Label>
										<Input
											id={`license-number-${license.id}`}
											type="text"
											value={license.licenseNumber}
											onChange={(e) =>
												updateLicense(license.id, {
													licenseNumber: e.target.value,
												})
											}
											required
										/>
									</div>
									<div className="space-y-1.5">
										<Label htmlFor={`license-expiry-${license.id}`}>
											Expiry Date
										</Label>
										<DatePicker
											id={`license-expiry-${license.id}`}
											value={license.expiryDate}
											onChange={(val) =>
												updateLicense(license.id, {
													expiryDate: val,
												})
											}
											minDate={new Date().toISOString().slice(0, 10)}
										/>
									</div>
								</div>
							</div>
						))}
						<button
							type="button"
							className="inline-flex cursor-pointer items-center gap-2 rounded-sm border border-(--color-border) bg-(--color-muted) px-[18px] py-2.5 font-medium text-(--color-muted-foreground) text-sm transition-all hover:bg-(--color-border) hover:text-(--color-foreground)"
							onClick={addLicense}
						>
							<Plus size={16} />
							Add License
						</button>
					</div>
				)}

				{/* Sanctions Screening Section */}
				{sanctionsFields.map((field) => (
					<FormField
						key={field.fieldId}
						config={field}
						value={formValues[field.fieldId]}
						onChange={handleChange}
						onBlur={handleBlur}
						error={fieldErrors[field.fieldId] || undefined}
					/>
				))}

				{/* Blockchain Monitoring Section */}
				{blockchainFields.map((field) => (
					<FormField
						key={field.fieldId}
						config={field}
						value={formValues[field.fieldId]}
						onChange={handleChange}
						onBlur={handleBlur}
						error={fieldErrors[field.fieldId] || undefined}
					/>
				))}

				{/* Regulatory Enforcement Section */}
				{enforcementFields.map((field) => (
					<FormField
						key={field.fieldId}
						config={field}
						value={formValues[field.fieldId]}
						onChange={handleChange}
						onBlur={handleBlur}
						error={fieldErrors[field.fieldId] || undefined}
					/>
				))}
			</FormSection>

			<FormSection title="Compliance Officer">
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<div className="space-y-1.5">
						<Label htmlFor="compliance-officer-name">
							Name
							<span
								aria-hidden="true"
								className="ml-0.5 text-(--color-error-500)"
							>
								*
							</span>
						</Label>
						<Input
							id="compliance-officer-name"
							type="text"
							value={formData.complianceOfficer.name}
							onChange={(e) =>
								updateFormData({
									complianceOfficer: {
										...formData.complianceOfficer,
										name: e.target.value,
									},
								})
							}
						/>
					</div>
					<div className="space-y-1.5">
						<Label htmlFor="compliance-officer-email">
							Email
							<span
								aria-hidden="true"
								className="ml-0.5 text-(--color-error-500)"
							>
								*
							</span>
						</Label>
						<Input
							id="compliance-officer-email"
							type="email"
							value={formData.complianceOfficer.email}
							onChange={(e) =>
								updateFormData({
									complianceOfficer: {
										...formData.complianceOfficer,
										email: e.target.value,
									},
								})
							}
						/>
					</div>
				</div>
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<div className="space-y-1.5">
						<Label htmlFor="compliance-officer-phone">
							Phone
							<span
								aria-hidden="true"
								className="ml-0.5 text-(--color-error-500)"
							>
								*
							</span>
						</Label>
						<Input
							id="compliance-officer-phone"
							type="tel"
							value={formData.complianceOfficer.phone}
							onChange={(e) =>
								updateFormData({
									complianceOfficer: {
										...formData.complianceOfficer,
										phone: e.target.value,
									},
								})
							}
						/>
					</div>
					<div className="space-y-1.5">
						<Label htmlFor="compliance-photo-id">Photo ID</Label>
						<FileUploadField
							compact
							id="compliance-photo-id"
							label="Photo ID"
							value={formData.complianceOfficer.photoIdUrl}
							onUpload={(key) =>
								updateFormData({
									complianceOfficer: {
										...formData.complianceOfficer,
										photoIdUrl: key,
									},
								})
							}
							onRemove={() =>
								updateFormData({
									complianceOfficer: {
										...formData.complianceOfficer,
										photoIdUrl: undefined,
									},
								})
							}
						/>
					</div>
				</div>
			</FormSection>

			<FormSection title="Optional Documents">
				<div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2">
					<FileUploadField
						id="risk-assessment"
						label="Latest Risk Assessment"
						accept=".pdf,.doc,.docx"
						description="Upload your latest risk assessment document"
						value={formData.riskAssessmentUrl}
						onUpload={(key) => updateFormData({ riskAssessmentUrl: key })}
						onRemove={() => updateFormData({ riskAssessmentUrl: undefined })}
					/>
					<FileUploadField
						id="compliance-program"
						label="Compliance Program/Policy"
						accept=".pdf,.doc,.docx"
						description="Upload your compliance program or policy document"
						value={formData.complianceProgramUrl}
						onUpload={(key) => updateFormData({ complianceProgramUrl: key })}
						onRemove={() => updateFormData({ complianceProgramUrl: undefined })}
					/>
				</div>
			</FormSection>
		</div>
	);
};

export default Step4Compliance;
